import type { StepUpRequiredDetails } from "@townsy/contracts";

/**
 * Staff confirmation (ADR-0039). When a staff request is answered STEP_UP_REQUIRED, `apiFetch` asks the registered
 * handler (the app's StepUpHost dialog: "enter your code") and, if the person confirmed, retries the request ONCE.
 * The browser never marks itself confirmed: the dialog posts the code to POST /me/two-factor/step-up and the server
 * records the confirmation against the session; the retry succeeds only because the server now says so.
 *
 * Several requests refused at the same moment share one dialog. After the person cancels, automatic prompts pause
 * briefly (a screen with several queries would otherwise reopen the dialog at once); an explicit "Enter code" button
 * calls `requestStepUp({ explicit: true })`, which always asks.
 */

export type StepUpHandler = (details: StepUpRequiredDetails | null) => Promise<boolean>;

let handler: StepUpHandler | null = null;
let pending: Promise<boolean> | null = null;
let declinedUntil = 0;

/** How long automatic prompts stay quiet after the person closed the dialog without confirming. */
export const STEP_UP_DECLINE_PAUSE_MS = 30_000;

/** The dialog host registers itself (returns the unregister function). */
export function setStepUpHandler(next: StepUpHandler | null): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = null;
  };
}

export const hasStepUpHandler = (): boolean => handler !== null;

/** Ask the person to confirm with a code; resolves true when the server accepted it. */
export function requestStepUp(details: StepUpRequiredDetails | null, opts: { explicit?: boolean; now?: number } = {}): Promise<boolean> {
  if (!handler) return Promise.resolve(false);
  if (pending) return pending;
  if (!opts.explicit && (opts.now ?? Date.now()) < declinedUntil) return Promise.resolve(false);
  const current = handler;
  pending = current(details)
    .catch(() => false)
    .then((ok) => {
      pending = null;
      declinedUntil = ok ? 0 : Date.now() + STEP_UP_DECLINE_PAUSE_MS;
      return ok;
    });
  return pending;
}

/** Tests only. */
export function resetStepUpForTests(): void {
  handler = null;
  pending = null;
  declinedUntil = 0;
}
